import type { HTMLAttributes, ReactNode } from 'react';

type Tone = 'default' | 'fresh' | 'hot';

/**
 * The toned variants are semantic, not decorative.
 *
 * `fresh` is for anything new - a first upload, a rising track, a discovery
 * signal. `hot` is for live rooms and nothing else. Using them for generic
 * success and error states would break the association the design leans on:
 * in this interface a red edge means somebody is broadcasting right now.
 */
const TONES: Record<Tone, string> = {
  default: 'border-line bg-surface',
  fresh: 'border-fresh-edge bg-fresh-wash',
  hot: 'border-hot-edge bg-hot-wash',
};

export function Card({
  children,
  tone = 'default',
  className = '',
  ...rest
}: {
  children: ReactNode;
  tone?: Tone;
  className?: string;
} & Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'children'>) {
  /*
   * The rest are forwarded, which they were not.
   *
   * Every `data-` hook put on a `<Card>` was silently dropped - the props were
   * accepted by TypeScript's excess-property tolerance through JSX spread and
   * then thrown away here. Nothing broke visibly; the attributes simply never
   * reached the DOM, which is exactly the kind of nothing that costs an hour
   * when a test cannot find an element that is plainly on screen.
   */
  return (
    <div className={`rounded-[var(--radius-card)] border ${TONES[tone]} ${className}`} {...rest}>
      {children}
    </div>
  );
}

/** A small mono pill for metadata: tags, counts, states. */
export function Chip({
  children,
  tone = 'default',
  className = '',
}: {
  children: ReactNode;
  tone?: 'default' | 'fresh' | 'hot' | 'signal';
  className?: string;
}) {
  const tones = {
    default: 'bg-raised text-faint',
    fresh: 'bg-fresh/12 text-fresh',
    hot: 'bg-hot/12 text-hot-text',
    signal: 'bg-accent/12 text-accent',
  };

  return (
    <span className={`meta-caps rounded-[var(--radius-chip)] px-2 py-1 ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
}
